import {
    DataTable,
    DataTablePresenter,
    getOptions,
    IFilterSchema,
    ISelectOption,
    ITableColumn,
    ITableFetchOptions,
    ListProviderChargemasterItemsOrderEnum,
    parseSorting2,
    PlaceOfServiceType,
    ProviderChargemaster,
    ProviderChargemasterItem,
    ProviderChargemasterItemSortType,
    renderAmount,
} from '@enter/core';
import { DataTableCard, PageLayout, PageLayoutBreadcrumb, PageLayoutBreadcrumbLink, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { CellProps } from 'react-table';
import { DialogContext } from 'services';
import { IChargemasterDetailRequest, IInjectedRootStore } from 'stores';
import { withRouter, RouterProps } from 'router/with-router';
import * as S from './chargemaster.detail.styles';

interface IChargemasterParams {
    chargemasterId: string;
}

type IChargemasterDetailProps = IInjectedRootStore & RouterProps<IChargemasterParams>;

interface IChargemasterDetailState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
    chargemaster: ProviderChargemaster | undefined;
    chargemasterItems: ProviderChargemasterItem[];
}

const columns: ITableColumn<ProviderChargemasterItem, ProviderChargemasterItemSortType>[] = [
    {
        id: 'procedure',
        Header: 'Procedure Code',
        accessor: 'procedure',
        sortType: ProviderChargemasterItemSortType.PROCEDURE,
    },
    {
        id: 'modifiers',
        Header: 'Modifiers',
        accessor: 'modifiers',
        sortType: ProviderChargemasterItemSortType.MODIFIER,
    },
    {
        id: 'amount',
        Header: () => <S.HeaderCurrency>Amount</S.HeaderCurrency>,
        accessor: 'amount',
        sortType: ProviderChargemasterItemSortType.AMOUNT,
        Cell: ({ cell }) => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'accepted',
        Header: () => <S.HeaderCurrency>Accepted</S.HeaderCurrency>,
        accessor: 'accepted',
        sortType: ProviderChargemasterItemSortType.ACCEPTED,
        Cell: ({ cell }) => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'expected',
        Header: () => <S.HeaderCurrency>Expected</S.HeaderCurrency>,
        accessor: 'expected',
        sortType: ProviderChargemasterItemSortType.EXPECTED,
        Cell: ({ cell }) => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'medicare',
        Header: () => <S.HeaderCurrency>Medicare</S.HeaderCurrency>,
        accessor: 'medicare',
        sortType: ProviderChargemasterItemSortType.MEDICARE,
        Cell: ({ cell }) => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'patient',
        Header: () => <S.HeaderCurrency>Patient</S.HeaderCurrency>,
        accessor: 'patient',
        sortType: ProviderChargemasterItemSortType.PATIENT,
        Cell: ({ cell }) => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'rvu',
        Header: 'RVU',
        accessor: 'rvu',
        sortType: ProviderChargemasterItemSortType.RVU,
    },
    {
        id: 'authorization',
        Header: 'Authorization',
        accessor: 'authorization',
        sortType: ProviderChargemasterItemSortType.AUTHORIZATION,
    },
    {
        id: 'paperwork',
        Header: 'Paperwork',
        accessor: 'paperwork',
        sortType: ProviderChargemasterItemSortType.PAPERWORK,
    },
    {
        id: 'description',
        Header: 'Description',
        accessor: 'description',
        sortType: ProviderChargemasterItemSortType.DESCRIPTION,
    },
    {
        id: 'placeOfService',
        Header: 'Place of Service',
        accessor: 'placeOfServiceType',
        sortType: ProviderChargemasterItemSortType.PLACEOFSERVICE,
    },
    {
        id: 'payer',
        Header: _.startCase('payer'),
        accessor: 'payer',
        disableSortBy: false,
        Cell: (props: CellProps<ProviderChargemasterItem>): React.ReactElement => {
            const payer = _.get(props.cell.row, 'original.payer', {});
            // ts-expect-error models will be updated
            return <span>{(payer as any)?.name ?? ''}</span>;
        },
    },
    {
        id: 'coverage',
        Header: 'Coverage',
        accessor: 'coverage',
        sortType: ProviderChargemasterItemSortType.COVERAGE,
    },
    {
        id: 'provider',
        Header: _.startCase('provider'),
        accessor: 'provider',
        disableSortBy: false,
        Cell: (props: CellProps<ProviderChargemasterItem>): React.ReactElement => {
            const provider = _.get(props.cell.row, 'original.provider', {});
            return (
                <span>
                    {(provider as any)?.first ?? ''} {(provider as any)?.last ?? ''}
                </span>
            );
        },
    },
];

@inject('rootStore')
@observer
class ChargemasterDetail extends React.Component<IChargemasterDetailProps, IChargemasterDetailState> {
    public static contextType = DialogContext;
    private presenter: DataTablePresenter<ProviderChargemasterItem>;

    constructor(props: IChargemasterDetailProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
            chargemaster: undefined,
            chargemasterItems: [],
        };

        const {
            rootStore: { claimStore },
        } = this.props;

        const filterSchemas: IFilterSchema<string>[] = [
            {
                label: 'Procedure Code',
                filterId: 'filterProcedure',
                columnId: 'procedure',
                filterType: 'String',
            },
            {
                label: 'Modifier',
                filterId: 'filterModifier',
                columnId: 'modifier',
                filterType: 'String',
            },
            {
                label: 'Amount',
                filterId: 'filterAmount',
                columnId: 'amount',
                filterType: 'Number',
            },
            {
                label: 'Place of Service',
                filterId: 'filterPlaceOfServiceType',
                columnId: 'placeOfService',
                filterType: 'Select',
                options: getOptions(PlaceOfServiceType),
            },
            {
                label: 'Payer',
                filterId: 'filterPayerId',
                columnId: 'payer',
                filterType: 'AsyncSelect',
                loadOptions: async (query): Promise<ISelectOption[]> => {
                    const payers = await claimStore.queryPayers(query);
                    return _.map(payers, p => ({
                        label: p.name,
                        value: p.id,
                    }));
                },
            },
            {
                label: 'Provider',
                filterId: 'filterProviderId',
                columnId: 'provider',
                filterType: 'AsyncSelect',
                loadOptions: async (query): Promise<ISelectOption[]> => {
                    const providers = await claimStore.queryProviders(query);
                    return _.map(providers, p => ({
                        label: `${p.first} ${p.last}`,
                        value: p.id,
                    }));
                },
            },
        ];

        this.presenter = new DataTablePresenter<ProviderChargemasterItem>({
            name: 'chargemasterDetail',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public render(): React.ReactElement {
        return (
            <PageLayout>
                <PageLayoutRow>
                    <PageLayoutBreadcrumb>
                        <PageLayoutBreadcrumbLink to="/chargemasters">Chargemasters</PageLayoutBreadcrumbLink>
                        <PageLayoutBreadcrumbLink to={`/chargemasters/${this.chargemasterId}`}>
                            {this.state.chargemaster && this.state.chargemaster.name}
                            {!this.state.chargemaster && '...'}
                        </PageLayoutBreadcrumbLink>
                    </PageLayoutBreadcrumb>
                </PageLayoutRow>
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable presenter={this.presenter} isLoading={this.state.loading} onFetch={this.onFetch} />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get chargemasterId(): string {
        return this.props.match.params.chargemasterId;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        const {
            rootStore: { chargemasterStore },
        } = this.props;
        this.setState({
            options: fetchOptions,
            loading: true,
        });
        try {
            if (!this.state.chargemaster) {
                const chargemaster = await chargemasterStore.loadChargemaster(this.chargemasterId);
                this.setState({ chargemaster });
            }
            const req = this.buildRequest(fetchOptions);
            const detail = await chargemasterStore.loadChargemasterDetail(req);
            this.presenter.onDataUpdate(detail.chargemasterItems || []);
            this.presenter.onDataTotalChange(detail.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.setState({ loading: false });
            if (err && err.payload) {
                if (
                    err.payload.code === 'err.access_denied' ||
                    err.payload.code === 'err.chargemaster_does_not_exist'
                ) {
                    if (this.props.history) {
                        this.props.history.push(`/chargemasters`);
                        return;
                    }
                }
            }
            (this.context as any).showError(err);
        }
    };

    private resolveSortColumn(sort: string[]): ProviderChargemasterItemSortType[] | undefined {
        const resolved = _.compact(
            _.map(sort, s => _.find(ProviderChargemasterItemSortType, (value: string): boolean => s === value)),
        );
        return !_.isEmpty(resolved) ? resolved : undefined; // NB: avoid empty query params
    }
    private resolveSortOrder(sort: string[]): ListProviderChargemasterItemsOrderEnum[] | undefined {
        const resolved = _.compact(
            _.map(sort, s => _.find(ListProviderChargemasterItemsOrderEnum, (value: string): boolean => s === value)),
        );
        return !_.isEmpty(resolved) ? resolved : undefined; // NB: avoid empty query params
    }

    private buildRequest(newOptions: ITableFetchOptions | undefined): IChargemasterDetailRequest {
        const req: IChargemasterDetailRequest = {
            chargemasterId: this.chargemasterId,
        };
        if (newOptions) {
            const { validFilters, pageSize, pageIndex, sortBy } = newOptions;
            const sortRequest = parseSorting2(sortBy, columns);
            const sortColumns = this.resolveSortColumn(sortRequest.sort);
            const sortOrders = this.resolveSortOrder(sortRequest.order);
            _.assign(req, {
                page: pageIndex,
                pageSize,
                sortColumns,
                sortOrders,
                filters: validFilters,
            });
        }
        return req;
    }
}

export default withRouter(ChargemasterDetail);
